import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { ArrowLeftOutlined, LockFilled } from '@ant-design/icons';
import { antGold, antGrey, runwayColors } from '../styles/globalStyles';
import { displayNameForCapability } from '../../helpers';
import { Tip } from '../setup/shared';
import { IntegrationsSetupViewComponent } from '../setup/steps/KeyIntegrations';
import { IntegrationCapability, IntegrationErrorType } from '../../types';
import { AppComponentProps } from '../../typescriptHelpers';
import { useOauthIntegrationInstaller } from '../setup/hooks';
import IntegrationConfiguration from '../IntegrationConfiguration';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIntegrationFromIntegrationType } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { useUpdateIntegrationModal } from '../setup/useUpdateIntegrationModal';

interface ConnectIntegrationsContainerProps extends AppComponentProps {
    capability: IntegrationCapability;
    onCloseConnectIntegrations: () => void;
}

export const ConnectIntegrationsContainer: React.FC<ConnectIntegrationsContainerProps> = ({
    appId,
    orgId,
    capability,
    onCloseConnectIntegrations,
}) => {
    const history = useHistory();

    const lastInstalledIntegrationType = history.location.state?.['lastInstalledIntegrationType'];

    const lastInstalledIntegration = useSelector((state: RootState) =>
        selectIntegrationFromIntegrationType(state, appId, lastInstalledIntegrationType),
    );

    React.useEffect(() => {
        if (
            lastInstalledIntegrationType &&
            lastInstalledIntegration &&
            lastInstalledIntegration?.integrationError?.status === IntegrationErrorType.IncompleteData
        ) {
            setShowUpdateIntegrationModal(true);
        }
    }, [lastInstalledIntegrationType, lastInstalledIntegration]);

    //handles configuring existing integration
    const { setShowUpdateIntegrationModal, updateIntegrationModalProps } = useUpdateIntegrationModal({
        orgId,
        appId,
        selectedIntegration: lastInstalledIntegration,
    });

    // to intercept oauth installation redirects
    useOauthIntegrationInstaller({});

    return (
        <div style={{ display: 'flex', flex: 1, zIndex: 0, overflow: 'auto' }}>
            <div style={{ flex: 1, padding: 36 }}>
                <IntegrationsSetupViewComponent
                    appId={appId}
                    orgId={orgId}
                    capability={capability}
                    onCompleteIntegrationSetup={onCloseConnectIntegrations}
                />
            </div>
            <Divider type="vertical" style={{ height: 'auto' }} />
            <div style={{ maxWidth: 480, flex: 1, padding: 36 }}>
                <Space direction="vertical" size={44} style={{ justifyContent: 'end', alignItems: 'end' }}>
                    <Text
                        style={{
                            fontSize: 24,
                            fontWeight: 400,
                            color: antGrey.grey8,
                            textAlign: 'right',
                            display: 'inline-block',
                        }}
                    >
                        Runway works better when it&apos;s connected to a {displayNameForCapability(capability)} tool.
                    </Text>
                    <Tip color={antGold.gold7} icon={LockFilled}>
                        <Text
                            style={{
                                color: antGrey.grey8,
                                fontSize: 14,
                                fontWeight: 400,
                                textAlign: 'right',
                                width: 221,
                            }}
                        >
                            All integrations are connected via official APIs, authenticated with most modern option
                            offered
                        </Text>
                    </Tip>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        style={{
                            backgroundColor: 'white',
                            color: runwayColors.primary,
                            width: '120px',
                            height: '40px',
                            borderStyle: 'solid',
                            borderColor: runwayColors.primary,
                            borderWidth: 1,
                            borderRadius: '8px',
                            fontSize: '16px',
                        }}
                        onClick={onCloseConnectIntegrations}
                    >
                        Go back
                    </Button>
                </Space>
                {lastInstalledIntegration && (
                    <IntegrationConfiguration
                        {...updateIntegrationModalProps}
                        onClose={() => {
                            setShowUpdateIntegrationModal(false);
                        }}
                    />
                )}
            </div>
        </div>
    );
};
